import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "../Icon";
import { WORK_CATEGORIES, WORK_CATEGORY_ICON } from "../data";
import { useWorks } from "../useWorks";
import { CtaBand, PageHead, PageTop, SectionHead, useSeo } from "../ui";

/* 개발 사례 — SSTOA '개발 사례 관리'에서 등록 · 공개한 것만 보인다.
   카드: 대표 사진 / 분야 / 제목 / 담당 범위 태그 / 한 줄 설명 / (자세히) 상세 내용 · 사진 · 영상 */

function Photo({ work }) {
  const [failed, setFailed] = useState(false);
  const src = work.images[0];
  const I = Icon[WORK_CATEGORY_ICON[work.category]];
  if (!src || failed) {
    return (
      <div className="wcard__thumb wcard__thumb--empty">
        {I ? <I width="30" height="30" aria-hidden="true" focusable="false" /> : null}
      </div>
    );
  }
  return (
    <div className="wcard__thumb">
      <img src={src} alt={work.title} loading="lazy" onError={() => setFailed(true)} />
    </div>
  );
}

/* 유튜브 — 클릭 전까지 iframe 을 띄우지 않고 nocookie 도메인을 쓴다 */
function Youtube({ id, title }) {
  const [play, setPlay] = useState(false);
  if (!id) return null;
  return play ? (
    <div className="yt">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
        title={`${title} 영상`}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  ) : (
    <button
      type="button"
      className="yt yt--poster"
      onClick={() => setPlay(true)}
      style={{ backgroundImage: `url(https://i.ytimg.com/vi/${id}/hqdefault.jpg)` }}
    >
      <span className="yt__play" aria-hidden="true" />
      <span className="sr-only">{title} 영상 재생</span>
    </button>
  );
}

function WorkCard({ w }) {
  const [open, setOpen] = useState(false);
  const more = w.images.slice(1);
  const hasDetail = Boolean(w.body) || more.length > 0 || Boolean(w.period);

  return (
    <article id={w.id} className="wcard">
      <Photo work={w} />
      <div className="wcard__body">
        <div className="wcard__meta">
          <span className="wcard__field">{w.field}</span>
          {w.mode && (
            <span className={`badge ${w.mode === "Partnership" ? "badge--accent" : ""}`}>{w.mode}</span>
          )}
        </div>
        <h3 className="wcard__title">{w.title}</h3>
        {w.scopeTags.length > 0 && (
          <ul className="scope-tags" aria-label="담당 범위">
            {w.scopeTags.map((t) => <li key={t}>{t}</li>)}
          </ul>
        )}
        <p className="wcard__desc">{w.summary}</p>

        {open && (
          <div className="wcard__detail">
            {w.body && <p className="wcard__text">{w.body}</p>}
            {more.map((src) => (
              <img key={src} src={src} alt={w.title} loading="lazy" className="wcard__more-img" />
            ))}
            {w.period && <p className="wcard__client">기간 {w.period}</p>}
          </div>
        )}

        <Youtube id={w.youtube} title={w.title} />
        {w.link && (
          <Link to={w.link.to} className="seg__link wcard__link">
            {w.link.label} <Icon.ArrowRight width="13" height="13" aria-hidden="true" focusable="false" />
          </Link>
        )}

        <div className="wcard__foot">
          <p className="wcard__client">고객 {w.client || "비공개"}</p>
          {hasDetail && (
            <button type="button" className="wcard__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? "접기" : "자세히"}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Works() {
  useSeo({
    title: "개발 사례 — 하드웨어 · 소프트웨어 · 웹서비스 개발 | I.META",
    description:
      "I.META 이름으로 수행한 개발 사례. 하드웨어 개발, 소프트웨어 개발, 웹서비스 개발로 나눠 담당 범위와 함께 정리했습니다.",
    path: "/works",
  });

  const { items, status } = useWorks();
  const { hash } = useLocation();

  /* 목록이 늦게 그려지므로, 홈 카드에서 /works#<사례> 로 넘어온 경우 받은 뒤에 스크롤한다 */
  useEffect(() => {
    if (status !== "ok" || !hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [status, hash]);

  return (
    <>
      <PageTop>
        <PageHead kicker="WORKS" title="개발 사례" />
      </PageTop>

      {status === "loading" && <p className="works-state">불러오는 중…</p>}
      {status === "error" && <p className="works-state">개발 사례를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>}

      {status === "ok" && (
        <>
          {/* 분류 바로가기 */}
          <nav className="work-tabs" aria-label="개발 사례 분류">
            {WORK_CATEGORIES.map((c) => (
              <a key={c.id} href={`#${c.id}`} className="work-tabs__item">
                {c.label}
                <span>{items.filter((w) => w.category === c.id).length}</span>
              </a>
            ))}
          </nav>

          {WORK_CATEGORIES.map((c) => {
            const list = items.filter((w) => w.category === c.id);
            if (list.length === 0) return null;
            return (
              <section key={c.id} id={c.id} className="section section--tight work-sec">
                <SectionHead title={c.label} />
                <div className="wgrid">
                  {list.map((w) => <WorkCard key={w.id} w={w} />)}
                </div>
              </section>
            );
          })}
        </>
      )}

      <CtaBand />
    </>
  );
}
